import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { GTColumnSelectAnchor, GTExpander, GTModifierLabel, GTTdm } from 'src/components/GenericTable';
import { formatDate, numeralFormat } from 'src/utils/misc';
import { filter } from 'lodash';
import { Link } from 'react-router-dom';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { checkIsModifierStrategy } from 'src/components/Modifier/util';
import { IncompleteStateMarkerNew, TextOverflowTooltip } from 'src/common';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import useVersion from 'src/hooks/useVersion';
import { encodeQueryParams } from 'src/api/client';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TLLink } from '../../../DashboardPage/comps';
import { ControlQuery, useUnlinkScenarioFromControlsMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';

type RiskManagementPlan = NonNullable<NonNullable<ControlQuery['control']>['riskManagementPlans']>[number];

type UseColumns = (a: { data?: RiskManagementPlan[]; controlId: string }) => {
  columns: ColumnDef<RiskManagementPlan>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = [
  'status_scen',
  'name',
  'source',
  'event',
  'consequence',
  'expected_loss',
  'updated_at',
  'edited_at',
  '_mod_applied',
];

export const useColumns: UseColumns = ({ data = [], controlId }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.controlScenarios}-columns`, defaultColumns);
  const { version, assessmentId } = useVersion();
  const { refetchQuery } = useCache();

  const [unlink] = useUnlinkScenarioFromControlsMutation({
    onCompleted: () => refetchQuery({ include: ['control'] }),
  });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm, GTInternalIds.expander];

    const list: ColumnDef<RiskManagementPlan>[] = [
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.scenario?.id,
        cell: (info) => info.getValue(),
      },
      {
        id: 'status_scen',
        header: 'Status',
        accessorFn: (row) => getScenarioStatusName(row.scenario?.status),
        cell: (info) => info.getValue(),
      },
      {
        id: 'assessment_name',
        header: 'Assessment',
        accessorFn: (row) => row.scenario?.assessment?.name,
        cell: (info) => info.getValue(),
        sortUndefined: 'last',
        sortingFn: sortingFns.text,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.scenario?.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/risk-assessments/${info.row.original.scenario?.assessment.id}/scenario/${
                info.row.original.scenario?.id
              }${version ? encodeQueryParams({ version, assessmentId }) : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['Control'],
                    params: {
                      id: controlId,
                    },
                  },
                  destination: { value: ['Scenario'], params: { id: info.row.original.scenario?.id } },
                  tags: ['RMP', 'SCENARIO'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortUndefined: 'last',
        sortingFn: sortingFns.text,
      },
      {
        id: 'source',
        header: 'Source',
        accessorFn: (row) => getRiskSourcesName(row.scenario?.source),
        cell: (info) => info.getValue(),
      },
      {
        id: 'event',
        header: 'Event',
        accessorFn: (row) => getRiskEventsName(row.scenario?.event),
        cell: (info) => info.getValue(),
      },
      {
        id: 'consequence',
        header: 'Consequence',
        accessorFn: (row) => getRiskConsequencesName(row.scenario?.consequence),
        cell: (info) => info.getValue(),
      },
      {
        id: 'risk_owner',
        header: 'Risk Owner',
        accessorFn: (row) => row.scenario?.ownerName,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'expected_loss',
        header: 'Expected Loss',
        accessorFn: (row) => row.scenario?.liveRisk?.expectedLoss,
        cell: (info) => numeralFormat(info.getValue() as number),
      },
      {
        id: '_control_count',
        header: 'Linked Controls',
        accessorFn: (row) => row.scenario?.riskManagementPlans?.length || 0,
        cell: (info) => info.getValue(),
      },
      {
        id: 'risk_identified_date',
        header: 'Identified Date',
        accessorFn: (row) => row.scenario?.riskIdentifiedDate || '',
        cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
      },
      {
        id: 'updated_at',
        header: 'Last Updated',
        accessorFn: (row) => row.scenario?.meta?.updatedAt || null,
        cell: ({ row }) => {
          const date = row.original.scenario?.meta?.updatedAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: 'edited_at',
        header: 'Last Edited Date',
        accessorFn: (row) => row.scenario?.editedAt || null,
        cell: ({ row }) => (
          <TLLink date={row.original.scenario?.editedAt}>
            {formatDate(row.original.scenario?.editedAt, { formatType: 'datetime' })}
          </TLLink>
        ),
      },
      {
        id: 'created_at',
        header: 'Created Date',
        accessorFn: (row) => row.scenario?.meta?.createdAt || null,
        cell: ({ row }) => {
          const date = row.original.scenario?.meta?.createdAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: '_mod_applied',
        header: 'Modifiers',
        accessorFn: (row) => row.modifiers?.length || 0,
        cell: (info) => {
          if (!checkIsModifierStrategy(info.row.original.control?.strategy)) return null;
          return <GTModifierLabel length={info.getValue() as number} plainText />;
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const onUnlinkScenarioClick = !version
            ? () => {
                unlink({
                  variables: {
                    scenarioId: row.original.scenario?.id!,
                    controlIds: [controlId],
                  },
                });
              }
            : undefined;
          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm
                onUnlinkScenarioClick={onUnlinkScenarioClick}
                extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original.scenario!} />}
              />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, controlId, version, assessmentId]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.controlScenarios}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'status_scen', title: 'Status' },
        { key: 'assessment_name', title: 'Assessment' },
        { key: 'name', title: 'Name' },
        { key: 'source', title: 'Source' },
        { key: 'event', title: 'Event' },
        { key: 'consequence', title: 'Consequence' },
        {
          key: 'risk_owner',
          title: 'Risk Owner',
        },
        {
          key: 'expected_loss',
          title: 'Expected Loss',
        },
        {
          key: '_control_count',
          title: 'Linked Controls',
        },
        {
          key: 'risk_identified_date',
          title: 'Identified Date',
        },
        {
          key: 'updated_at',
          title: 'Last Updated',
        },
        {
          key: 'edited_at',
          title: 'Last Edited Date',
        },
        {
          key: 'created_at',
          title: 'Created Date',
        },
        { key: '_mod_applied', title: 'Modifiers' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, GTColumnSelectAnchorExported };
};
